import {
  Article,
  ArticleAction,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useRemoveLineItem } from "@babackapp/return-widget-components/hooks/use-remove-line-item";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import type {
  DraftLineItem,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import { Separator } from "@babackapp/ui/components/separator";
import { EnterIcon } from "@radix-ui/react-icons";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useWidgetParams } from "../hooks/use-widget-params";

export function ReturnRequestSummary() {
  const { orderId, shopId, logoUrl } = useWidgetParams();
  const navigate = useNavigate();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const summary = useFinancialSummary(orderId, order ?? null);
  const removeLineItem = useRemoveLineItem(orderId);

  const handleRemove = (lineItemId: string) => {
    const currentCount = draft?.lineItems.length ?? 0;
    removeLineItem(lineItemId);
    if (currentCount <= 1) {
      navigate(`/return-widget/${shopId}/order/${orderId}/line-item`);
    }
  };

  const exchangeItems =
    draft?.lineItems.filter(
      (li) =>
        li.resolution === "exchange" || li.resolution === "exchange-credit",
    ) ?? [];
  const refundItems =
    draft?.lineItems.filter((li) => li.resolution === "refund") ?? [];
  const discountItems =
    draft?.lineItems.filter((li) => li.resolution === "discount") ?? [];

  return (
    <WidgetPage>
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>Résumé de votre demande de retour</HeaderTitle>
        <HeaderDescription>
          Vérifiez les articles sélectionnés avant de confirmer.
        </HeaderDescription>
      </Header>

      <div className="w-full mt-4 flex flex-col gap-6">
        {exchangeItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>Articles à échanger</SectionHeader>
            <ArticleList size="sm">
              {exchangeItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={order?.lineItems ?? []}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}

        {refundItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>Articles à rembourser</SectionHeader>
            <ArticleList size="sm">
              {refundItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={order?.lineItems ?? []}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}

        {discountItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>Articles avec bon de réduction</SectionHeader>
            <ArticleList size="sm">
              {discountItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={order?.lineItems ?? []}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}

        <Separator />

        {summary.refundTotal > 0 && (
          <ReceiptLine>
            <ReceiptLineLabel>Total à rembourser</ReceiptLineLabel>
            <ReceiptLineValue>
              {formatPrice(summary.refundTotal, summary.currency)}
            </ReceiptLineValue>
          </ReceiptLine>
        )}
        {summary.discountTotal > 0 && (
          <ReceiptLine>
            <ReceiptLineLabel>Bon de réduction</ReceiptLineLabel>
            <ReceiptLineValue>
              {formatPrice(summary.discountTotal, summary.currency)}
            </ReceiptLineValue>
          </ReceiptLine>
        )}
      </div>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ enter: true, exit: true }}
          layout="position"
        >
          <Link to="../line-item">
            <Button variant="secondary">
              <PlusIcon />
              Ajouter un autre article
            </Button>
          </Link>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer
          layoutId="line-item-confirm-button"
          variants={{}}
        >
          <Link to="../return-mode">
            <Button>
              <EnterIcon />
              Confirmer
            </Button>
          </Link>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}

type SummaryLineItemProps = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  onRemove: () => void;
};

function SummaryLineItem({
  item,
  orderLineItems,
  onRemove,
}: SummaryLineItemProps) {
  const { title, subtitle, image } = getLineItemDisplay(
    orderLineItems,
    item.lineItemId,
  );
  return (
    <Article>
      <ArticleImage
        src={image ?? undefined}
        layoutId={`img-${item.lineItemId}`}
      />
      <ArticleTitle>{title}</ArticleTitle>
      {subtitle && (
        <ArticleSubtitle>
          {subtitle} — ×{item.quantity}
        </ArticleSubtitle>
      )}
      {!subtitle && <ArticleSubtitle>×{item.quantity}</ArticleSubtitle>}
      <ArticleAction autoHide={false}>
        <Button variant="outline" size="icon-sm" onClick={onRemove}>
          <Trash2Icon />
        </Button>
      </ArticleAction>
    </Article>
  );
}
